/**
 * PreparationTimerUI - Displays the authoritative preparation phase timer.
 *
 * Responsibilities:
 * - Display the remaining preparation time in seconds from the backend
 * - Show a locked state when the timer expires (preparation phase ended)
 * - React to PreparationTimerUpdate and state change events
 * - Provide reactive render data for UI binding
 * - Indicate when the timer is not applicable (combat phase, no match)
 *
 * The timer value is always authoritative from the backend. The frontend
 * never computes or counts down independently; it only displays what
 * the server sends. The server now broadcasts the countdown every second,
 * so there is NO local countdown here (the old local countdown ran on an
 * independent client clock and drifted from the server, then hard-reset on
 * sync — the "timer jumping back and forth" bug).
 */

import { AppShell, AppShellEvent } from '../AppShell';

// ─── Timer Render Data ──────────────────────────────────────────────────────

/**
 * Data exposed for rendering the preparation timer.
 */
export interface PreparationTimerRenderData {
  /** Whether the timer UI should be visible (in preparation phase with active timer). */
  isVisible: boolean;

  /** Remaining seconds from the authoritative backend timer. Null if not applicable. */
  remainingSeconds: number | null;

  /** Whether the preparation phase is locked (timer expired or phase is not preparation). */
  isLocked: boolean;

  /** The total timer duration for this round (from RoundStarted). Null if unknown. */
  totalSeconds: number | null;

  /** Whether the timer is currently ticking (in preparation phase with a valid timer). */
  isActive: boolean;

  /** The current phase: 'preparation', 'combat', or 'eliminated'. */
  phase: string;

  /** Percentage of time remaining (0-100). Null if timer is not active. */
  percentageRemaining: number | null;
}

/**
 * Callback invoked when the timer render data changes.
 */
export type PreparationTimerRenderCallback = (data: PreparationTimerRenderData) => void;

/**
 * Callback invoked when the timer reaches the locked state.
 */
export type PreparationTimerLockedCallback = () => void;

// ─── Preparation Timer UI ───────────────────────────────────────────────────

export class PreparationTimerUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: PreparationTimerRenderCallback[] = [];
  private _lockedCallbacks: PreparationTimerLockedCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;
  private _wasLocked: boolean = false;
  /** Tracks the initial total timer duration for percentage calculation. */
  private _totalDuration: number | null = null;
  /**
   * Smooth local countdown (whole seconds), driven by a local 1s interval so
   * the display ticks every second even if authoritative updates arrive less
   * frequently (throttled tab, network jitter, or a stale/older server build
   * that broadcasts coarsely). It is RE-ANCHORED to the server value on EVERY
   * new authoritative value — so it never drifts and never hard-jumps the way
   * the old drift-thresholded sync did (the "9, 7, 3, 6, 5, 1" bug).
   */
  private _localRemaining: number | null = null;
  private _localTimer: ReturnType<typeof setInterval> | null = null;
  /** The last authoritative value we anchored to (detects a new value). */
  private _lastServerValue: number | null = null;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from AppShell state). */
  get renderData(): PreparationTimerRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the timer UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the preparation timer UI and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;
    this._wasLocked = false;
    this._totalDuration = null;
    this._localRemaining = null;
    this._lastServerValue = null;
    this._startLocalCountdown();

    // Listen for app shell events to trigger render updates
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Initial render
    this._notifyRenderCallbacks();
  }

  /**
   * Hide the preparation timer UI and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;
    this._wasLocked = false;
    this._totalDuration = null;
    this._localRemaining = null;
    this._lastServerValue = null;
    this._stopLocalCountdown();

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Start the smooth local countdown (decrements whole seconds every 1s).
   * The display is driven by THIS interval, so it ticks every second no matter
   * how often authoritative updates arrive; each new server value re-anchors it.
   */
  private _startLocalCountdown(): void {
    this._stopLocalCountdown();
    this._localTimer = setInterval(() => {
      if (this._localRemaining !== null && this._localRemaining > 0) {
        this._localRemaining--;
        this._notifyRenderCallbacks();
      }
    }, 1000);
  }

  /** Stop the local countdown interval. */
  private _stopLocalCountdown(): void {
    if (this._localTimer) {
      clearInterval(this._localTimer);
      this._localTimer = null;
    }
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: PreparationTimerRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to timer lock events.
   * Returns an unsubscribe function.
   */
  onLocked(callback: PreparationTimerLockedCallback): () => void {
    this._lockedCallbacks.push(callback);
    return () => {
      const index = this._lockedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._lockedCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const matchState = this._appShell.gameState.match;
        // The per-player phase is STALE during combat: the server flips
        // every player to 'combat' in endPreparationPhase but only
        // broadcasts it in the post-combat state update — the match-level
        // phase (set on CombatStarted) is live. Prefer it the same way
        // MatchHUD does, so an early-start round (everyone ready before the
        // countdown hits 0) stops the countdown the moment combat begins
        // instead of ticking it down through the battle.
        const playerPhase = matchState.players.find((p: any) => p.id === this._playerId)?.phase;
        const phase = matchState.phase === 'combat'
          ? 'combat'
          : (playerPhase ?? matchState.phase);

        // Re-anchor the smooth local countdown to the authoritative server
        // value on EVERY new value (not just when drift exceeds a threshold —
        // that was the old "jumping" bug). The local interval then counts down
        // between updates, so the display is always smooth.
        if (phase === 'preparation' && matchState.preparationTimerSeconds !== null) {
          const serverValue = matchState.preparationTimerSeconds;
          if (this._localRemaining === null || serverValue !== this._lastServerValue) {
            this._localRemaining = serverValue;
            this._lastServerValue = serverValue;
          }
        }

        // Leaving preparation (combat / eliminated): stop the countdown.
        if (phase !== 'preparation') {
          this._localRemaining = 0;
          this._lastServerValue = null;
        }

        this._checkLockTransition();
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  /**
   * Detects when the timer transitions to locked state and fires callbacks.
   */
  private _checkLockTransition(): void {
    const data = this._buildRenderData();
    if (data.isLocked && !this._wasLocked) {
      this._wasLocked = true;
      this._notifyLockedCallbacks();
    } else if (!data.isLocked) {
      this._wasLocked = false;
    }
  }

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyLockedCallbacks(): void {
    for (const callback of this._lockedCallbacks) {
      callback();
    }
  }

  private _buildRenderData(): PreparationTimerRenderData {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;

    // Find current player's data
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;
    // Stale per-player phase during combat — same convention as
    // _onAppEvent and MatchHUD: the match-level 'combat' (set on
    // CombatStarted) wins, so an early-start round reads as combat (locked,
    // not counting down) from the first combat frame.
    const phase = matchState.phase === 'combat'
      ? 'combat'
      : (currentPlayer?.phase ?? matchState.phase);

    // Display the smooth local countdown (driven by the local 1s interval and
    // re-anchored to the server on every new value). Fall back to the raw
    // server value if the local countdown hasn't anchored yet.
    const remainingSeconds = this._localRemaining !== null
      ? Math.max(0, this._localRemaining)
      : matchState.preparationTimerSeconds;

    // Phase-based state
    const isInPreparation = phase === 'preparation';
    const isLocked = !isInPreparation || remainingSeconds === 0 || remainingSeconds === null;
    const isActive = isInPreparation && remainingSeconds !== null && remainingSeconds > 0;

    // Track total duration: capture it the first time we see a timer value in preparation
    // This happens when RoundStarted sets the timer or when the match first enters preparation
    if (isActive && remainingSeconds !== null && this._totalDuration === null) {
      this._totalDuration = remainingSeconds;
    }
    // Reset total when timer becomes inactive (new round or phase change).
    if (!isActive) {
      this._totalDuration = null;
    }

    const totalSeconds = this._totalDuration;

    // Percentage calculation
    let percentageRemaining: number | null = null;
    if (isActive && totalSeconds !== null && totalSeconds > 0) {
      percentageRemaining = Math.min(100, Math.max(0, (remainingSeconds / totalSeconds) * 100));
    }

    // Timer is visible when in a match and in preparation phase
    const isVisible = this._appShell.isInMatch && isInPreparation;

    return {
      isVisible,
      remainingSeconds,
      isLocked,
      totalSeconds,
      isActive,
      phase,
      percentageRemaining,
    };
  }
}
